import { DATE_FORMATS, TIME_FORMATS } from 'common/constants';
import { IDateFilter } from 'common/interfaces';
import { IStatsSearchCriteria } from 'common/interfaces/idashboard';
import moment from 'moment';

export default class DateUtil {}

export const dateFormatter = (dateString: Date) => {
  const formattedDate = moment.utc(dateString).local().format(`${TIME_FORMATS.HMM}a, ${DATE_FORMATS.DDMMM}`);
  return formattedDate;
};

export const dateFormatterWithYear = (dateString: Date) => {
  const formattedDate = moment.utc(dateString).local().format(`${TIME_FORMATS.HM} a ${DATE_FORMATS.DDMMMYYYY}`);
  return formattedDate;
};

export const dateFormatterWithoutTime = (dateString: Date) => {
  const formattedDate = moment.utc(dateString).local().format(DATE_FORMATS.DDMMYYYY);
  return formattedDate;
};

export const formatDateTime = (date: Date | undefined, format?: string | undefined) => {
  if (!date) {
    return '';
  }
  const formattedDate = date
    ? moment
        .utc(date)
        .local()
        .format(format || DATE_FORMATS.DDMMYYYY_hhmmss)
    : undefined;
  return formattedDate;
};

export const formatDateToMMMYYYY = (date: string) => {
  return date ? moment.utc(date).format(DATE_FORMATS.MMM_YYYY) : date;
};

export const formatDateStringToDate = (date: string, format: string = DATE_FORMATS.YYYYMMDD) => {
  return date ? moment.utc(date, format).toDate() : undefined;
};

export const getFormattedDate = (plusDays: number = 0, format: string = DATE_FORMATS.YYYYMMDD) => {
  return moment().add(plusDays, 'day').format(format);
};

export const format24HrTo12HrTime = (time: string | undefined, format?: string | undefined) => {
  if (!time) {
    return '';
  }
  const formattedDate = time ? moment(time, DATE_FORMATS.HHmm).format(format || DATE_FORMATS.hmm_a) : undefined;
  return formattedDate;
};

export const format12HrTo24HrTime = (time: string | undefined, format?: string | undefined) => {
  if (!time) {
    return '';
  }
  const formattedDate = time ? moment(time, DATE_FORMATS.hmm_a).format(format || DATE_FORMATS.HHmmss) : undefined;
  return formattedDate;
};

export const formatDate = (dateString: string, fromFormat: string, toFormat: string) => {
  return moment.utc(dateString, fromFormat).local().format(toFormat);
};

export const getDisplayDate = (
  dateString: string | undefined,
  fromFormat: string = DATE_FORMATS.YYYYMMDD,
  finalFormat?: string,
) => {
  return dateString
    ? moment
        .utc(dateString, fromFormat)
        // .local()
        .format(finalFormat || DATE_FORMATS.DDMMYYYY)
    : '';
};

export const getDisplayDateWithTime = (
  dateString: string,
  fromFormat: string = DATE_FORMATS.YYYYMMDD_HHmmssa,
  finalFormat?: string,
) => {
  return moment
    .utc(dateString, fromFormat)
    .local()
    .format(finalFormat || DATE_FORMATS.MMDDYYYY_hhmmssa);
};

const msInDay = 24 * 60 * 60 * 1000;

export const getComparableDateRange = (current: IDateFilter) => {
  const start = moment(current.startDate, DATE_FORMATS.YYYYMMDD);
  const end = moment(current.endDate, DATE_FORMATS.YYYYMMDD);

  const isFullMonth = start.date() === 1 && end.date() === end.clone().endOf('month').date();

  if (isFullMonth) {
    const previousStart = start.clone().subtract(1, 'month').startOf('month');
    const previousEnd = previousStart.clone().endOf('month');

    return {
      startDate: previousStart.format(DATE_FORMATS.YYYYMMDD),
      endDate: previousEnd.format(DATE_FORMATS.YYYYMMDD),
    } as IDateFilter;
  }

  // Fallback: Use duration-based shift
  const rangeLength = end.diff(start, 'days') + 1;
  const previousStart = start.clone().subtract(rangeLength, 'days');
  const previousEnd = end.clone().subtract(rangeLength, 'days');

  return {
    startDate: previousStart.format(DATE_FORMATS.YYYYMMDD),
    endDate: previousEnd.format(DATE_FORMATS.YYYYMMDD),
  } as IDateFilter;
};

export const getFullDateRange = (range: IStatsSearchCriteria) => {
  const { startDate, endDate, compareStartDate, compareEndDate } = range;

  const generateDates = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const current = new Date(start);
    const last = new Date(end);

    if (isNaN(current.getTime()) || isNaN(last.getTime())) {
      throw new Error('Invalid date format');
    }

    while (current <= last) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const primaryRange = generateDates(startDate, endDate);
  const compareRange = compareStartDate && compareEndDate ? generateDates(compareStartDate, compareEndDate) : [];

  return {
    primaryRange,
    compareRange,
  };
};
